/**
 * @fileoverview Search By Keywords Component
 * @active Used in current Next.js App Router routes
 *
 * This component provides a search input field for filtering companies by keywords.
 * It allows users to enter search terms and submit them to filter the companies list.
 *
 * Key functionality:
 * - Renders a search input field with a search icon button
 * - Uses react-hook-form for form handling
 * - Submits search queries to filter companies
 * - Triggers API requests with the search text parameter
 *
 * This component is used in the NewCompaniesCards component to allow users
 * to search and filter the displayed company cards by keywords.
 *
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - Parent component
 * @see {@link src/hooks/useCompaniesListRequest.js} - Hook for API requests
 * @see {@link src/assets/svg/search.svg} - Search icon
 */

import { memo } from "react";

import { Button, Flex, Icon, Input } from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";

import Search from "@/assets/svg/search.svg";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";

/**
 * Search By Keywords component
 *
 * This component provides a search input field that allows users to filter
 * the companies list by entering keywords. It submits the search query to
 * the API when the user clicks the search button or presses Enter.
 *
 * @returns {JSX.Element} Rendered component
 *
 * @example
 * <SearchByKeywords />
 */

const SearchByKeywords = () => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      search: "",
    },
  });

  const { getCompanies } = useCompaniesListRequest();

  const onSubmitHandler = ({ search }) => {
    getCompanies({
      params: {
        text: search || undefined,
      },
    });
  };

  return (
    <Controller
      control={control}
      name="search"
      render={({ field }) => (
        <Flex
          align={"center"}
          as={"form"}
          borderRadius={"4px"}
          bg={"menuGray"}
          ps={"12px"}
          h={"40px"}
          onSubmit={handleSubmit(onSubmitHandler)}
        >
          <Input
            variant={"unstyled"}
            placeholder="Filter by keyword..."
            _placeholder={{ paddingTop: "9px" }}
            {...field}
          />
          <Button variant={"unstyled"} type="submit">
            <Icon as={Search} />
          </Button>
        </Flex>
      )}
    />
  );
};

export default memo(SearchByKeywords);
